/**
 * ExportGranularSelector — granularidade × período × filtros do relatório.
 *
 * Campos obrigatórios surgem conforme o tipo selecionado (espelho das refines
 * do DTO): requesterPersonId p/ titular-export, operationId p/ cascade-chain,
 * datas p/ período customizado. As options de granularidade mapeiam para o
 * enum REAL do BE (titular/operationId/period/requestType) — handoff §2.
 */

import {
  GRANULARITY_LABEL,
  LGPD_REQUEST_TYPES,
  PERIOD_LABEL,
  REPORT_GRANULARITIES,
  REPORT_PERIODS,
  REQUEST_TYPE_LABEL,
} from './types';

import type { ReportFormState } from './report-form';
import type {
  LgpdReportRequestType,
  LgpdReportType,
  ReportGranularity,
  ReportPeriod,
} from './types';

export function ExportGranularSelector({
  reportType,
  form,
  onChange,
}: {
  reportType: LgpdReportType;
  form: ReportFormState;
  onChange: (patch: Partial<ReportFormState>) => void;
}): JSX.Element {
  const needsTitular = reportType === 'titular-export';
  const needsOperationId = reportType === 'cascade-chain';
  const isCustom = form.period === 'custom';

  return (
    <div className="rep-form-grid">
      <label className="rep-field">
        <span className="rep-field__label">Granularidade</span>
        <select
          className="rep-select"
          value={form.granularity}
          onChange={(e) => {
            onChange({ granularity: e.target.value as ReportGranularity });
          }}
        >
          {REPORT_GRANULARITIES.map((g) => (
            <option key={g} value={g}>
              {GRANULARITY_LABEL[g]}
            </option>
          ))}
        </select>
      </label>

      <label className="rep-field">
        <span className="rep-field__label">Período</span>
        <select
          className="rep-select"
          value={form.period}
          onChange={(e) => {
            onChange({ period: e.target.value as ReportPeriod });
          }}
        >
          {REPORT_PERIODS.map((p) => (
            <option key={p} value={p}>
              {PERIOD_LABEL[p]}
            </option>
          ))}
        </select>
      </label>

      {isCustom && (
        <>
          <label className="rep-field">
            <span className="rep-field__label">Data inicial</span>
            <input
              type="date"
              className="rep-input"
              value={form.periodStart}
              onChange={(e) => {
                onChange({ periodStart: e.target.value });
              }}
            />
          </label>
          <label className="rep-field">
            <span className="rep-field__label">Data final</span>
            <input
              type="date"
              className="rep-input"
              value={form.periodEnd}
              onChange={(e) => {
                onChange({ periodEnd: e.target.value });
              }}
            />
          </label>
        </>
      )}

      {needsTitular && (
        <label className="rep-field rep-form-grid__span">
          <span className="rep-field__label">ID do titular · requesterPersonId *</span>
          <input
            className="rep-input"
            placeholder="ex: person_01H..."
            value={form.requesterPersonId}
            onChange={(e) => {
              onChange({ requesterPersonId: e.target.value });
            }}
          />
        </label>
      )}

      {needsOperationId && (
        <label className="rep-field rep-form-grid__span">
          <span className="rep-field__label">Identificador da operação raiz *</span>
          <input
            className="rep-input"
            placeholder="ex: op_..."
            value={form.operationId}
            onChange={(e) => {
              onChange({ operationId: e.target.value });
            }}
          />
        </label>
      )}

      <label className="rep-field rep-form-grid__span">
        <span className="rep-field__label">Filtrar por tipo de pedido (opcional)</span>
        <select
          className="rep-select"
          value={form.requestType}
          onChange={(e) => {
            onChange({ requestType: e.target.value as LgpdReportRequestType | '' });
          }}
        >
          <option value="">Todos os tipos</option>
          {LGPD_REQUEST_TYPES.map((t) => (
            <option key={t} value={t}>
              {REQUEST_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
